import React, {useMemo, useState} from 'react'
import PropTypes from "prop-types";
import BoldTypography from "components/BoldTypography";
import Tr from "translations/Translations";
import {makeStyles, Table, TableBody, TableCell, TableHead, TableRow} from "@material-ui/core";
import EditDescriptionPopup, {createEditDescriptionFile} from "components/popup/EditDescriptionPopup";
import MembersDocumentsListTableRow from "./MembersDocumentsListTableRow";
import UploadFile from "components/UploadFile";
import TitledComponent from 'components/TitledComponent';
import SelectMembersDocumentType from "./SelectMembersDocumentType";
import PurchasingGroupStage1Service from "services/PurchasingGroupStage1Service";
import clsx from "clsx";
import useSnackbar from "hooks/useSnackbar";
import WorkableButton from "components/button/WorkableButton";
import PurchasingGroupSectionService from "services/PurchasingGroupSectionService";

const useStyle = makeStyles(theme => ({
    lpColumn: theme.components.table.lpColumn,
    buttonColumn: theme.components.table.buttonColumn,
    dateColumn: theme.components.table.dateColumn,
    typeColumn: {
        width: 180
    },
    marginTop: {
        marginTop: theme.spacing(2)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    }

}));


function MembersDocumentsList(
    {
        groupId,
        mainDocs,
        editable,
        coordinatorView,
        membersDocs,
        memberId,
        validationSection,
        allMemberDocumentsApiPath
    }) {

    const classes = useStyle();
    const {documents, availableTypes} = membersDocs;
    const memberMode = !!memberId;
    const [editDescriptionFile, setEditDescriptionFile] = useState(false);
    const [generateWorking, setGenerateWorking] = useState(false);
    const [addFilesWorking, setAddFilesWorking] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState(null);
    const [configuredFiles, setConfiguredFiles] = useState(null);
    const {enqueueSuccessSnackbar, enqueueErrorSnackbar} = useSnackbar();

    const hasUpload = useMemo(() => {
        return editable && availableTypes && Object.keys(availableTypes).length > 0
    }, [availableTypes, editable])

    const openEditDescription = (file) => {
        if (memberMode) {
            setEditDescriptionFile(createEditDescriptionFile(groupId, {
                memberId,
                documentId: file.identity
            }, file.name, file.description || "", !editable || !file.actions.editDescription, false, false))
        } else {
            setEditDescriptionFile(createEditDescriptionFile(groupId, {
                documentId: file.identity,
                documentsApiPathType: allMemberDocumentsApiPath,
                }, file.name, file.description || "", !editable || !file.actions.editDescription, false, false))
        }
    }

    const generateGroupAgreementDocument = () => {
        setGenerateWorking(true);
        PurchasingGroupStage1Service.generateGroupAgreementDocument(groupId).then((resp) => {
            PurchasingGroupSectionService.getSections(groupId, validationSection.section);
            enqueueSuccessSnackbar(availableTypes.group_agreement);
        }).catch((e) => {
            enqueueErrorSnackbar(e.message)
        }).finally(() => {
            setGenerateWorking(false)
        })
    }

    const afterDocumentAdd = (docs) => {
        docs.filter(d => d.error !== null).forEach( d => enqueueErrorSnackbar(`${d.name} - ${d.typeTranslated}: ${d.error}`));
        PurchasingGroupSectionService.getSections(groupId, validationSection.section);
    }

    const handleSendFiles = (uploadedFiles, config) => {
        if (uploadedFiles.length) {
            const tab = uploadedFiles.map((x) => ({
                repositoryId: x.identity,
                type: config[`item${x.originalIndex}`]
            }));
            if (memberMode) {
                setAddFilesWorking(true);
                PurchasingGroupStage1Service.addMemberDocuments(groupId, memberId, tab).then(afterDocumentAdd).catch((e) => {
                    enqueueErrorSnackbar(e.message)
                }).finally(()=> setAddFilesWorking(false));
            } else {
                setAddFilesWorking(true);
                PurchasingGroupStage1Service.addAllMembersDocuments(groupId, allMemberDocumentsApiPath, tab).then(afterDocumentAdd).catch((e) => {
                    enqueueErrorSnackbar(e.message)
                }).finally(()=> setAddFilesWorking(false));
            }
        }
    }


    const handleSubmitSelectDocumentTypes = (values) => {
        setConfiguredFiles({files: selectedFiles, config: values})
        setSelectedFiles(null);
    };

    return (<>
        {!!documents && <div>

            <>
                <TitledComponent className={clsx(classes.marginTop, {
                    [classes.marginBottom]: coordinatorView
                })} title={Tr(memberMode ? 'documents' : 'purchasing_group_documents')}>
                    {!memberMode && !coordinatorView && !!availableTypes && !!availableTypes.group_agreement &&
                        <WorkableButton working={generateWorking} disabled={generateWorking} onClick={generateGroupAgreementDocument}>{`${Tr('create')} ${availableTypes.group_agreement}`}</WorkableButton>}

                    {!documents.length ?
                        <BoldTypography align='center' variant="h6">{Tr('no_options')}</BoldTypography>
                        :
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.lpColumn}>{Tr("column_lp")}</TableCell>
                                    <TableCell>{Tr("name")}</TableCell>
                                    <TableCell className={classes.buttonColumn}>{Tr("description")}</TableCell>
                                    <TableCell className={classes.typeColumn}>{Tr("type")}</TableCell>
                                    {!mainDocs && <TableCell className={classes.dateColumn}>{Tr("add_data")}</TableCell>}
                                    {mainDocs && <TableCell className={classes.dateColumn}>{Tr("publish_data")}</TableCell>}
                                    {mainDocs && !coordinatorView && <TableCell className={classes.dateColumn}>{Tr("status")}</TableCell>}
                                    <TableCell className={classes.buttonColumn}></TableCell>
                                    {!coordinatorView && mainDocs && <TableCell className={classes.buttonColumn}></TableCell>}
                                    {(!coordinatorView || (coordinatorView && memberMode)) && <TableCell className={classes.buttonColumn}></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    documents.map((doc, index) => (
                                        <MembersDocumentsListTableRow
                                            key={doc.identity}
                                            groupId={groupId}
                                            mainDocs={mainDocs}
                                            editable={editable}
                                            coordinatorView={coordinatorView}
                                            memberId={memberId}
                                            row={doc}
                                            index={index + 1}
                                            onEditDescriptionFile={openEditDescription}
                                            validationSection={validationSection}
                                            allMemberDocumentsApiPath={allMemberDocumentsApiPath}
                                        />))
                                }
                            </TableBody>
                        </Table>
                    }
                    {hasUpload &&
                        <UploadFile
                            onUploadPopupClosed={handleSendFiles}
                            onOpenConfigureFiles={(files) => setSelectedFiles(files)}
                            configuredFiles={configuredFiles}
                            working={addFilesWorking}
                        />
                    }
                </TitledComponent>
                {Boolean(editDescriptionFile) &&
                    <EditDescriptionPopup
                        editDescriptionFile={editDescriptionFile}
                        onUpdated={() => setEditDescriptionFile(null)}
                        onClose={() => setEditDescriptionFile(null)}
                        service={{updateDescription: memberMode ? PurchasingGroupStage1Service.updateMemberDocument : PurchasingGroupStage1Service.updateAllMembersDocument}}
                    />
                }

                {!!selectedFiles && <SelectMembersDocumentType
                    items={selectedFiles}
                    types={availableTypes}
                    onCancel={() => setSelectedFiles(null)}
                    onSubmit={handleSubmitSelectDocumentTypes}
                />}
            </>


        </div>
        }
    </>)
}

MembersDocumentsList.propTypes = {
    groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    mainDocs: PropTypes.bool,
    editable: PropTypes.bool.isRequired,
    coordinatorView: PropTypes.bool.isRequired,
    membersDocs: PropTypes.shape({
        documents: PropTypes.array.isRequired,
        availableTypes: PropTypes.object.isRequired,
    }).isRequired,
    memberId: PropTypes.number,
}

export default MembersDocumentsList;